import { toast } from 'sonner';

export function formatDuration(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function decodeHtml(html: string): string {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

export function formatDurationFromMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

export async function generateHMACSignature(secret: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const msgData = encoder.encode(message);
  const cryptoKey = await crypto.subtle.importKey(
    'raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, msgData);
  return Array.from(new Uint8Array(signature)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function generateRandomHex(length = 64): string {
  const array = new Uint8Array(length / 2);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function callSiteApi(
  siteUrl: string,
  publicKey: string,
  secretKey: string,
  route: string,
  method = 'GET',
  body?: string
) {
  const formattedUrl = siteUrl.replace(/\/+$/, '');
  if (formattedUrl.startsWith('http://')) {
    console.warn(`[AgencyPlus] Request to ${formattedUrl} is over HTTP. Custom headers (X-AgencyPlus-*) may be lost if the server redirects to HTTPS. Ensure your site URL in the dashboard matches the canonical protocol.`);
  }
  const endpoint = `${formattedUrl}/wp-json/agencyplus/v1/${route}`;
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const routePath = route.split('?')[0].replace(/\/+$/, '').replace(/^\/+/, '');
  const canonicalString = `${timestamp}\n${method}\n/agencyplus/v1/${routePath}\n${body ?? ''}`;
  const signature = await generateHMACSignature(secretKey, canonicalString);
  const response = await fetch(endpoint, {
    method,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      'X-AgencyPlus-Public-Key': publicKey,
      'X-AgencyPlus-Signature': signature,
      'X-AgencyPlus-Timestamp': timestamp,
    },
    ...(body ? { body } : {}),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || errorData?.code || `HTTP ${response.status}`;
    
    // Detect clock sync errors
    if (errorMessage.toLowerCase().includes('timestamp') && (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('future'))) {
      toast.error('Clock sync error', {
        description: 'Your device clock appears to be incorrect. Please sync your time in system settings to connect to the site.',
        duration: 10000,
      });
    }
    
    throw new Error(errorMessage);
  }
  return response.json();
}
